import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lightbulb, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SectionHeading } from "./Story";
import { getSuggestions, createSuggestion } from "@/lib/api";

export const Suggestions = ({ suggestions }) => {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [activity, setActivity] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => getSuggestions().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !activity.trim()) {
      toast.error("Please add your name and an idea.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createSuggestion({ name: name.trim(), activity: activity.trim(), note: note.trim() });
      setName(""); setActivity(""); setNote("");
      if (res.pending) {
        toast.success("Thanks! Your idea is in — it'll show once approved. 🎉");
      } else {
        toast.success("Love it! Thanks for the idea. 🎉");
        load();
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="suggestions" className="py-20 sm:py-28 lg:py-32 bg-[var(--surface)]" data-testid="suggestions-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <SectionHeading script={suggestions?.subtitle || "your ideas"} title={suggestions?.title || "Help Shape the Celebration"} testid="suggestions-heading" />
        {suggestions?.description && (
          <p className="text-center text-[var(--text-secondary)] max-w-2xl mx-auto -mt-6 mb-10 leading-relaxed">{suggestions.description}</p>
        )}

        <form onSubmit={submit} className="bg-[var(--card)] rounded-2xl p-6 sm:p-8 border border-[var(--border)] shadow-sm max-w-xl mx-auto space-y-4" data-testid="suggestions-form">
          <div>
            <Label>Your Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" maxLength={80} className="mt-1.5" data-testid="suggestions-name-input" />
          </div>
          <div>
            <Label>Your Idea</Label>
            <Input value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="e.g., A surprise flash-mob dance!" maxLength={120} className="mt-1.5" data-testid="suggestions-activity-input" />
          </div>
          <div>
            <Label>A little more (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any details that would help us make it happen..." maxLength={300} className="mt-1.5" data-testid="suggestions-note-input" />
          </div>
          <button type="submit" disabled={submitting} className="w-full rounded-full py-3 font-medium flex items-center justify-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-60" style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }} data-testid="suggestions-submit-button">
            <Send className="w-4 h-4" /> {submitting ? "Sending..." : "Share my idea"}
          </button>
        </form>

        {items.length > 0 && (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suggestions-wall">
            {items.map((it, i) => (
              <motion.div
                key={it.id || i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
                className="rounded-2xl p-5 border border-[var(--border-gold)] bg-[var(--card)] shadow-sm"
                data-testid={`suggestions-entry-${i}`}
              >
                <Lightbulb className="w-4 h-4 text-[var(--gold)]" />
                <p className="mt-2 font-medium text-[var(--text-primary)]">{it.activity}</p>
                {it.note && <p className="mt-1 text-sm text-[var(--text-secondary)] leading-relaxed">{it.note}</p>}
                <p className="mt-3 text-sm font-serif text-[var(--burgundy)]">— {it.name}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
